import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/axiosClient";
import { useAuth } from "../context/AuthContext";
import { BrandMark, BRAND_PROGRAM } from "../components/Brand";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!token) return <Navigate to="/forgot-password" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don’t match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      // The reset endpoint already sets fresh cookies for us; refresh the
      // auth context so the rest of the app knows we’re signed in.
      await refresh();
      setDone(true);
      toast.success("Password updated. You’re signed in.");
      setTimeout(() => navigate("/quiz", { replace: true }), 1200);
    } catch (err) {
      setError(formatApiError(err, "Couldn’t reset the password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="inline-flex items-center gap-3 mb-8">
          <BrandMark className="h-10 w-auto" />
          <span className="h-7 w-px bg-[#E8E2D9]" aria-hidden="true" />
          <Link
            to="/"
            className="font-serif-display text-base text-[#2D2A26] font-semibold hover:text-[#C86B5E] transition-colors"
          >
            {BRAND_PROGRAM}
          </Link>
        </div>

        <div className="bg-white border border-[#E8E2D9] rounded-3xl p-8 sm:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
          {done ? (
            <div className="text-center" data-testid="reset-done">
              <div className="w-12 h-12 mx-auto rounded-full bg-[#EBF2EA] flex items-center justify-center mb-4">
                <CheckCircle2 className="w-5 h-5 text-[#8A9A86]" strokeWidth={2} />
              </div>
              <h1 className="font-serif-display text-2xl text-[#2D2A26] font-semibold">All set.</h1>
              <p className="text-sm text-[#5C5A56] mt-2">
                Your password has been updated. Redirecting you to the check-in…
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-serif-display text-3xl text-[#2D2A26] font-semibold">Choose a new password</h1>
              <p className="mt-2 text-sm text-[#5C5A56]">Use at least 6 characters.</p>
              <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8A8782] mb-2">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      data-testid="reset-password-input"
                      className="w-full px-5 py-3 pr-14 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] focus:outline-none focus:border-[#C86B5E] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      data-testid="reset-toggle-password"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full hover:bg-[#FDF5EE] flex items-center justify-center text-[#8A8782] hover:text-[#C86B5E] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8A8782] mb-2">
                    Confirm password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); if (error) setError(""); }}
                    required
                    autoComplete="new-password"
                    data-testid="reset-confirm-input"
                    className="w-full px-5 py-3 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] focus:outline-none focus:border-[#C86B5E] transition-colors"
                  />
                </div>
                {error && (
                  <div className="rounded-2xl bg-[#FDF5EE] border border-[#F8EBE9] p-3 text-sm text-[#C86B5E]" data-testid="reset-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="reset-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#C86B5E] text-white rounded-full px-7 py-3.5 font-medium hover:bg-[#B35B4E] disabled:opacity-60 transition-all"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Update password <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
